import { useCallback } from "react";

import { walkTree } from "utils/treeUtils";
import { nodeTypes } from "variables";
import { useProjectDataStore, useProjectUIStore } from "stores/projectStore";

export const useExpandCollapseActions = () => {
  const treeRef = useProjectDataStore((state) => state.treeRef);
  const setOpenMap = useProjectUIStore((state) => state.setOpenMap);
  const openMap = useProjectUIStore((state) => state.getOpenMap());

  const expandAll = useCallback(() => {
    treeRef?.current?.openAll && treeRef?.current.openAll();
    setOpenMap({});
  }, [setOpenMap, treeRef]);

  const collapseAll = useCallback(() => {
    treeRef?.current?.closeAll && treeRef?.current.closeAll();

    const rootNode = treeRef?.current?.root.children[0];
    rootNode && treeRef?.current.open(rootNode);
    const newMap = {};
    walkTree({
      node: rootNode,
      childAccessor: (node) => node.children,
      callback: (node) => {
        if (node.data.kind !== nodeTypes.ROOT) {
          newMap[node.data.id] = false;
        }
      },
    });
    setOpenMap(newMap);
  }, [setOpenMap, treeRef]);

  const collapseLevel = useCallback(() => {
    const rootNode = treeRef?.current?.root.children[0];
    const newOpenMap = { ...openMap };
    const nodesToClose = new Set();

    let currentMaxLevel = 0;

    walkTree({
      node: rootNode,
      childAccessor: (node) => node.children,
      skipChildren: (node) => !node.isOpen,
      callback: (node) => {
        if (
          node.isOpen &&
          node.children.length > 0 &&
          node.level >= currentMaxLevel
        ) {
          if (node.level === currentMaxLevel) {
            nodesToClose.add(node);
          } else {
            currentMaxLevel = node.level;
            nodesToClose.clear();
            nodesToClose.add(node);
          }
        }
      },
    });
    if (currentMaxLevel > 0) {
      nodesToClose.forEach((node) => {
        node.close();
        newOpenMap[node.id] = false;
      });
    }
    setOpenMap(newOpenMap);
  }, [openMap, setOpenMap, treeRef]);

  const expandLevel = useCallback(() => {
    const rootNode = treeRef?.current?.root.children[0];
    const newOpenMap = { ...openMap };
    const nodesToOpen = new Set();

    // Assigned 1000 as a value which will most likely never actually be surpased by actual node levels
    let currentMinLevel = 1000;

    walkTree({
      node: rootNode,
      childAccessor: (node) => node.children,
      skipChildren: (node) => !node.isOpen,
      callback: (node) => {
        if (
          !node.isOpen &&
          node.children &&
          node.children.length > 0 &&
          node.level <= currentMinLevel
        ) {
          if (node.level === currentMinLevel) {
            nodesToOpen.add(node);
          } else {
            currentMinLevel = node.level;
            nodesToOpen.clear();
            nodesToOpen.add(node);
          }
        }
      },
    });
    if (currentMinLevel < 1000) {
      nodesToOpen.forEach((node) => {
        node.open();
        delete newOpenMap[node.id];
      });
    }
    setOpenMap(newOpenMap);
  }, [openMap, setOpenMap, treeRef]);

  return {
    collapseAll,
    collapseLevel,
    expandAll,
    expandLevel,
  };
};
